
import Vue from 'vue';
import { VueGoodTable } from 'vue-good-table';
import 'vue-good-table/dist/vue-good-table.css';
import { Component, Inject, Prop, Watch } from 'vue-property-decorator';
import Toasted from 'vue-toasted';
import { exposeHeaders, handleError, initClient } from '../../../api';
import { blobDownload, formatDate, WindowType } from '../../../util';
import { PqtApiUiV2Client, PqtArchiveRequest, PqtMeasurementSpecification, PqtProduct, PqtProductTest } from '../../backend/backendapi';
import ArchiveModal from '../archivemodal/ArchiveModal';
import { RiskOption } from '../archivemodal/archivemodal.model';
import PqtEditModal from '../edit/PqtEditModal';
import PqtProductTestEditModal from '../test/PqtProductTestEditModal';
import PqtImportModal from './PqtImportModal';
import { paginationOptions, searchOptions } from '../../../util/vue-good-table';
import State from '../State';
import { Results, VgtParameters, VgtRemoteManger } from '../../../util/VgtRemoteManger';
import MeasurementSpecificationsTemplateBadges from '../../testequipment/measurementSpecificationTemplates/MeasurementSpecificationsTemplateBadges';

declare var window: WindowType;

Vue.use(Toasted, { duration: 2000 });

@Component({
  components: {
    ArchiveModal,
    MeasurementSpecificationsTemplateBadges,
    PqtEditModal,
    PqtProductTestEditModal,
    PqtImportModal,
    VueGoodTable,
  },
})
export default class PqtList extends Vue {
  @Prop({ default: '' })
  readonly filter!: string;
  @Prop({ default: true })
  readonly editable!: boolean;
  @Prop({ default: false })
  readonly showArchiveFields!: boolean;

  @Inject({ from: 'state' })
  readonly state!: State;

  //runtime
  newProductFiles: string[] = [];
  vgtManager = new VgtRemoteManger<PqtProduct>(this.loadDevices.bind(this), 'en', [
    { field: 'combinedIdentifier', type: 'string' },
    { field: 'lastTester', type: 'string' },
    { field: 'lastTestDate', type: 'date' },
    { field: 'name', type: 'string' },
    { field: 'responsible', type: 'string' },
    { field: 'status', type: 'string' },
    { field: 'ident', type: 'string' },
    { field: 'costCenter', type: 'string' },
  ]);

  //api
  private readonly productsApi = exposeHeaders(initClient(PqtApiUiV2Client), 'listProducts');

  //config
  selectConfig = {};

  get canUpload(): boolean {
    return window.application.permissions.productQualityTests.edit;
  };

  get columns(): { label: string, field: unknown }[] {
    let configuredColumns = (!this.showArchiveFields ? this.state.config.unarchivedListFields : this.state.config.archivedListFields) ?? [];
    if (!configuredColumns.length) {
      //use defaults
      configuredColumns = !this.showArchiveFields
          ? ['product.combinedIdentifier', 'product.ident', 'product.name', 'product.lastTester', 'product.lastTestDate', 'product.responsible',
            'product.measurementSpecifications']
          : ['product.combinedIdentifier', 'product.ident', 'product.name', 'product.lastTester', 'product.lastTestDate', 'product.responsible',
            'product.measurementSpecifications', 'product.archiveReason', 'product.archiveRisk'];
    }
    const ret: { label: string, field: unknown, [key: string]: unknown }[] = configuredColumns
        .map(c => {
          switch (c) {
            case 'product.combinedIdentifier':
              return {
                label: this.$t('productQualityTests.list.title').toString(),
                field: 'combinedIdentifier',
              };
            case 'product.ident':
              return {
                label: this.$t('productQualityTests.list.ident').toString(),
                field: 'ident',
              };
            case 'product.name':
              return {
                label: this.$t('productQualityTests.list.name').toString(),
                field: 'name',
              };
            case 'product.lastTester':
              return {
                label: this.$t('productQualityTests.list.lastTester').toString(),
                field: 'lastTester',
              };
            case 'product.lastTestDate':
              return {
                label: this.$t('productQualityTests.list.lastTestDate').toString(),
                field: 'lastTestDate',
                type: 'date',
                formatFn: (lastTestDate: Date | undefined) => lastTestDate ? formatDate(lastTestDate) : '',
              };
            case 'product.responsible':
              return {
                label: this.$t('productQualityTests.list.responsible').toString(),
                field: 'responsible',
              };
            case 'product.placeOfUse':
              return {
                label: this.$t('productQualityTests.list.placeOfUse').toString(),
                field: 'placeOfUse',
              };
            case 'product.measurementSpecifications':
              return {
                label: this.$t('productQualityTests.list.measurementSpecificationTemplates').toString(),
                field: 'measurementSpecifications',
                sortable: false,
              };
            case 'product.archiveReason':
              return {
                label: this.$t('productQualityTests.list.archiveReason').toString(),
                field: 'archiveReason',
              };
            case 'product.archiveRisk':
              return {
                label: this.$t('productQualityTests.list.archiveRisk').toString(),
                field: (row: PqtProduct) => {
                  let localRiskOptions = this.state.riskOptionsSetting[this.$i18n.locale] as RiskOption[];
                  localRiskOptions ??= this.state.riskOptionsSetting['en'] as RiskOption[];
                  if (!localRiskOptions) {
                    return row.archiveRisk!;
                  }
                  return localRiskOptions.find(e => e.id === row.archiveRisk!)?.name ?? row.archiveRisk!;
                },
              };
            default:
              return null;
          }
        })
        .filter(Boolean)
        .map(e => e!);

    if (this.editable) {
      ret.push(...[{
        label: this.$t('general.actions').toString(),
        field: 'actions',
      }]);
    }

    return ret;
  }

  get paginationOptions(): any {
    return paginationOptions(this.$i18n);
  }

  get searchOptions(): any {
    return searchOptions(this.$i18n);
  }

  get windowArchive(): boolean {
    return window.application.permissions.productQualityTests.archive;
  }

  get windowAudit(): boolean {
    return window.application.permissions.productQualityTests.audit;
  }

  get windowEdit(): boolean {
    return window.application.permissions.productQualityTests.edit;
  }

  mounted(): void {
    //only load if not already loaded by onFilterChanged()
    if (!this.vgtManager.results.data) {
      this.vgtManager.reload();
    }
  }

  createNew(): void {
    const product = PqtProduct.fromJS({ id: 0, issueDate: new Date(), entryDate: new Date(), itemNo: 'xxx', files: [], measurementSpecifications: [] });
    this.newProductFiles.splice(0);
    (this.$refs.productModal as PqtEditModal).open(product, []);
  }

  async loadDevices(params: VgtParameters): Promise<Results<PqtProduct>> {
    const response = await this.productsApi.listProducts(params.filter, params.sort, params.page, params.pageSize);
    return { data: response.content, totalRows: parseInt(response.headers.get('total') ?? '0') };
  }

  async save(productFinal: PqtProduct): Promise<void> {
    if (productFinal.id < 1) {
      let productFromServer: PqtProduct;
      try {
        const res = await this.productsApi.createProducts([productFinal]);
        this.$toasted.info(this.$t('txtSaved').toString());
        await this.vgtManager.reload();
        (this.$refs.productModal as PqtEditModal).dismiss();
        productFromServer = res[0];
      } catch (err) {
        handleError(this.$toasted, this.$i18n, err);
        return;
      }

      //link files
      for (let i = 0; i < this.newProductFiles.length; i++) {
        await this.productsApi.linkProductMedia(productFromServer.id, this.newProductFiles[i])
            .then(r => {
              productFinal.files.push(r!);
            })
            .catch(err => handleError(this.$toasted, this.$i18n, err));
      }
    } else {
      await this.productsApi.updateProduct(productFinal.id, productFinal)
          .then(r => {
            this.$toasted.info(this.$t('txtSaved').toString());
            this.vgtManager.reload();
            (this.$refs.productModal as PqtEditModal).dismiss();
          })
          .catch(err => handleError(this.$toasted, this.$i18n, err));
    }
  }

  async productFileAdded(event: { fileId: string, product: PqtProduct }): Promise<void> {
    console.log('productFileAdded', event.fileId);
    if (!event.product.id) {
      this.newProductFiles.push(event.fileId); //update media in save/create
    } else {
      await this.productsApi.linkProductMedia(event.product.id, event.fileId)
          .then(r => {
            this.$toasted.info(this.$t('txtSaved').toString());
            event.product.files.push(r!);
          })
          .catch(err => handleError(this.$toasted, this.$i18n, err));
    }
  }

  async onClick(arg: { row: PqtProduct, pageIndex: number, selected: boolean, event: MouseEvent }): Promise<void> {
    this.newProductFiles.splice(0);
    let tests: PqtProductTest[] | null = null;
    try {
      tests = await this.productsApi.listTests(arg.row.id);
    } catch (err) {
      handleError(this.$toasted, this.$i18n, err);
    }
    (this.$refs.productModal as PqtEditModal).open(PqtProduct.fromJS(arg.row), tests);
  }

  onArchive(row: PqtProduct): void {
    (this.$refs.archivemodal as ArchiveModal).open(row);
  }

  onArchiveConfirm(model: PqtArchiveRequest): void {
    this.productsApi.archiveProduct(model.id, model)
        .then(r => {
          (this.$refs.archivemodal as ArchiveModal).dismiss();
          this.vgtManager.reload();
          this.$toasted.info(this.$t('txtSaved').toString());
        })
        .catch(err => handleError(this.$toasted, this.$i18n, err));
  }

  onAddTest(row: PqtProduct): void {
    const test = PqtProductTest.fromJS({
      id: 0,
      productId: row.id,
      testDate: new Date(),
      testSpecification: row.testSpecification,
      expectedError: row.errorMargin,
      files: [],
      measurementSpecifications: row.measurementSpecifications?.map(e => PqtMeasurementSpecification.fromJS(e)) ?? [],
      measurements: [],
    });
    (this.$refs.testModal as PqtProductTestEditModal).open(row, test);
  }

  genCSV(): string {
    const table = this.$refs.table as VueGoodTable;

    const cols = this.columns.filter(e => e.field !== 'actions');
    const csv = '\ufeff' //utf8 bom (byte order marker)
        + cols.map(e => e.label).join(';') + '\n'
        + ((table.paginated?.length ?? 0) === 0 ? '' : table.paginated[0].children.map(row => {
          return cols.map(col => {
            if (typeof col.field === 'string') {
              return ('' + (row[col.field] ?? '')).split(';').join(',');
            } else if (typeof col.field === 'function') {
              return ('' + (col.field(row) ?? '')).split(';').join(',');
            } else {
              return 'cannot extract value';
            }
          }).join(';');
        }).join('\n'));

    console.log(csv);
    return csv;
  }

  exportCSV(): void {
    const data = this.genCSV();
    const fileName = 'export_product.csv';

    const blob = new Blob([data], { type: 'octet/stream' });
    blobDownload(blob, fileName);
  }


  openImportModal(): void {
    (this.$refs.importModal as PqtImportModal).open();
  }

  @Watch('filter', { immediate: true })
  onFilterChanged(newValue: string, oldValue: string): void {
    if (newValue === oldValue) {
      return;
    }
    this.vgtManager.extraFilter = this.filter;
  }

  @Watch('$i18n.locale', { immediate: true })
  onLocaleChanged(newValue: string, oldValue: string): void {
    if (newValue === oldValue) {
      return;
    }
    this.vgtManager.dateFormat = this.$t('dates.dateFormat.momentJs').toString();
  }
}

